import * as THREE from "three";

/**
 * Moteur d'indicateurs : le pendant front de api/experiments/terrain3d/indicators.py.
 *
 * PRINCIPE. Le front ne connait plus les donnees d'un territoire. Chaque
 * couche du datapack embarque la DESCRIPTION de ce qu'elle sait afficher
 * (`indicators: [...]`), calculee au moment de la generation sur les donnees
 * reelles : bornes observees, modalites presentes, couleurs de la source. Ce
 * module ne fait que rendre cette description — couleurs, legende, libelles,
 * formats.
 *
 * Consequence directe : ajouter un indicateur ne touche AUCUN fichier JS, et
 * la meme mecanique sert les communes, la geologie et toute couche a venir
 * qui produira des specifications du meme format.
 *
 * La seule chose que le front garde en propre, ce sont les PALETTES : une
 * specification designe sa rampe par une cle (`ramp: "drought"`), jamais par
 * des couleurs. Les palettes restent donc au meme endroit que le reste du
 * rendu 3D, ou elles s'accordent avec le terrain, le ciel et les autres
 * nappes.
 */

// --- Palettes ---------------------------------------------------------------

// Chaque rampe est une liste de [position 0-1, [r, g, b]]. Elles sont pensees
// pour se lire SUR un relief vert/ocre en semi-transparence : une rampe qui
// passerait par les bruns se confondrait avec le terrain lui-meme.

// Presence humaine : sable clair -> violet profond.
const POPULATION = [
  [0.0, [246, 239, 217]],
  [0.35, [242, 190, 130]],
  [0.65, [219, 116, 106]],
  [0.85, [150, 62, 110]],
  [1.0, [74, 30, 84]],
];

// Restriction d'eau, dans les codes couleur des arretes secheresse
// (vigilance -> alerte -> alerte renforcee -> crise). Sert aussi aux echelles
// ordonnees de risque et de vulnerabilite, qui se lisent dans le meme sens.
const DROUGHT = [
  [0.0, [214, 232, 224]],
  [0.3, [246, 222, 140]],
  [0.6, [237, 160, 70]],
  [0.85, [206, 79, 52]],
  [1.0, [122, 27, 33]],
];

// Eau mobilisee / prelevee : blanc-bleu -> bleu profond.
const WATER = [
  [0.0, [237, 245, 247]],
  [0.3, [168, 214, 227]],
  [0.6, [86, 158, 199]],
  [0.85, [37, 97, 158]],
  [1.0, [16, 48, 96]],
];

// Emprise agricole : creme -> vert soutenu.
const FARMLAND = [
  [0.0, [247, 243, 219]],
  [0.35, [211, 224, 152]],
  [0.65, [148, 189, 100]],
  [0.85, [86, 143, 64]],
  [1.0, [36, 88, 43]],
];

// Rampe divergente pour les evolutions : rouge (perte) -> beige neutre ->
// bleu-vert (gain). Le neutre TOMBE AU MILIEU, c'est tout son interet : une
// rampe sequentielle rendrait "stable" indiscernable de "legere baisse".
const DIVERGENT = [
  [0.0, [140, 45, 40]],
  [0.25, [214, 126, 96]],
  [0.5, [242, 238, 224]],
  [0.75, [116, 180, 172]],
  [1.0, [22, 92, 100]],
];

// Precipitations : convention "carte meteo" (sec -> humide).
const PRECIP = [
  [0.0, [224, 196, 128]],
  [0.3, [214, 214, 120]],
  [0.55, [125, 195, 130]],
  [0.8, [70, 140, 210]],
  [1.0, [115, 60, 180]],
];

// ETP : demande evaporative, lecture inverse (fort = le milieu perd de
// l'eau) -> rampe chaude, du sable au brun brule.
const ETP = [
  [0.0, [245, 238, 210]],
  [0.3, [244, 208, 130]],
  [0.6, [232, 150, 78]],
  [0.85, [199, 88, 55]],
  [1.0, [120, 40, 34]],
];

// Infiltration : ce qui descend vers les nappes -> bleus profonds, distincts
// du ruissellement pour que les deux moities du bilan se comparent d'un coup
// d'oeil.
const GROUNDWATER = [
  [0.0, [240, 240, 233]],
  [0.35, [176, 205, 214]],
  [0.65, [102, 158, 184]],
  [0.85, [48, 100, 145]],
  [1.0, [21, 45, 92]],
];

// Ruissellement : ecoulement de surface -> turquoise vers vert d'eau.
const RUNOFF = [
  [0.0, [243, 241, 226]],
  [0.35, [196, 224, 197]],
  [0.65, [116, 189, 176]],
  [0.85, [50, 133, 145]],
  [1.0, [18, 70, 88]],
];

// Temperature : bleu froid -> rouge sombre.
const TEMPERATURE = [
  [0.0, [64, 108, 176]],
  [0.3, [148, 194, 214]],
  [0.5, [244, 232, 190]],
  [0.75, [226, 143, 82]],
  [1.0, [142, 38, 40]],
];

// Humidite des sols (SWI) : 0 = sec. Lecture inversee des precipitations —
// ocre brule a gauche, vert humide a droite.
const SOIL = [
  [0.0, [156, 96, 48]],
  [0.3, [214, 176, 116]],
  [0.6, [212, 214, 156]],
  [0.85, [136, 186, 132]],
  [1.0, [56, 128, 96]],
];

export const RAMPS = {
  population: POPULATION,
  drought: DROUGHT,
  water: WATER,
  farmland: FARMLAND,
  divergent: DIVERGENT,
  precip: PRECIP,
  etp: ETP,
  groundwater: GROUNDWATER,
  runoff: RUNOFF,
  temperature: TEMPERATURE,
  soil: SOIL,
};

const FALLBACK_RAMP = WATER;

// Couleur d'une entite sans valeur pour l'indicateur affiche. Volontairement
// grise et desaturee : "non renseigne" ne doit pas se confondre avec une
// modalite de la legende. Un `null` a toujours un sens dans la source
// (section non couverte, secret statistique, commune fusionnee).
export const NODATA_COLOR = "#8d9aa0";

// Palette de secours pour les categories dont la source ne fournit pas de
// couleur (milieu de circulation, culture dominante...). Teintes espacees
// en Ton, saturation moyenne : lisibles en semi-transparence sur le relief,
// et distinctes les unes des autres jusqu'a une dizaine de modalites.
const CATEGORICAL_FALLBACK = [
  "#4e79a7", "#f28e2b", "#59a14f", "#b07aa1", "#76b7b2",
  "#edc948", "#e15759", "#9c755f", "#8cd17d", "#bab0ac",
];

// Oui / non : vert d'eau pour "oui", ardoise pour "non". Pas de rouge — un
// booleen geologique (nappe libre, karstique) n'est ni bon ni mauvais, il
// decrit.
const BOOLEAN_COLORS = { true: "#2f8f83", false: "#7c8a95" };

export function rampFor(spec) {
  return RAMPS[spec?.ramp] || FALLBACK_RAMP;
}

function rampRgb(t, ramp) {
  const clamped = Math.min(1, Math.max(0, t));
  for (let i = 0; i < ramp.length - 1; i++) {
    const [t0, c0] = ramp[i];
    const [t1, c1] = ramp[i + 1];
    if (clamped >= t0 && clamped <= t1) {
      const f = (clamped - t0) / (t1 - t0 || 1);
      return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f];
    }
  }
  return ramp[ramp.length - 1][1];
}

export function rampCss(ramp) {
  const stops = (ramp || FALLBACK_RAMP).map(
    ([t, c]) => `rgb(${c[0]},${c[1]},${c[2]}) ${(t * 100).toFixed(0)}%`
  );
  return `linear-gradient(90deg, ${stops.join(", ")})`;
}

/** Degrade CSS de la legende, construit depuis la MEME rampe que le rendu 3D. */
export function rampToCssGradient(spec) {
  return rampCss(rampFor(spec));
}

function rampColorAt(t, ramp) {
  const [r, g, b] = rampRgb(t, ramp);
  // Les couleurs de rampe sont exprimees en sRGB (comme les hex de la
  // source) : le dire explicitement evite une nappe delavee par rapport aux
  // teintes categorielles, converties, elles, par THREE.Color(hex).
  return new THREE.Color().setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace);
}

function rampHex(t, ramp) {
  const [r, g, b] = rampRgb(t, ramp);
  return `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
}

// --- Formats ----------------------------------------------------------------

const FORMATS = {
  count: (v) => Math.round(v).toLocaleString("fr-FR"),
  decimal: (v) => v.toLocaleString("fr-FR", { maximumFractionDigits: 1 }),
  precise: (v) => v.toLocaleString("fr-FR", { maximumFractionDigits: 2 }),
};

export function formatNumber(value, format = "decimal") {
  if (typeof value !== "number" || !Number.isFinite(value)) return "n/d";
  return (FORMATS[format] || FORMATS.decimal)(value);
}

/** Valeur d'un indicateur pour une entite, prete a afficher (unite comprise). */
export function formatIndicatorValue(spec, record) {
  if (!spec || !record) return "n/d";
  const value = record[spec.id];
  if (value == null) return "n/d";
  if (spec.kind === "boolean") return value ? spec.trueLabel : spec.falseLabel;
  if (spec.kind === "category" || spec.kind === "ordinal") return levelLabel(spec, value);
  const formatted = formatNumber(value, spec.format);
  return spec.unit ? `${formatted} ${spec.unit}` : formatted;
}

function levelLabel(spec, value) {
  const level = (spec.levels || []).find((l) => l.value === value);
  return level ? level.label : String(value);
}

// --- Selection --------------------------------------------------------------

export function findIndicator(specs, indicatorId) {
  if (!specs || !specs.length) return null;
  return specs.find((s) => s.id === indicatorId) || specs[0];
}

/**
 * Indicateurs regroupes par `group`, dans l'ordre d'apparition des
 * specifications : le pipeline les emet deja dans un ordre pense (le plus
 * parlant en premier), inutile de les retrier ici.
 */
export function groupedIndicators(specs) {
  const groups = [];
  const byName = new Map();
  (specs || []).forEach((spec) => {
    let group = byName.get(spec.group);
    if (!group) {
      group = { name: spec.group, indicators: [] };
      byName.set(spec.group, group);
      groups.push(group);
    }
    group.indicators.push(spec);
  });
  return groups;
}

// --- Legende et couleurs ----------------------------------------------------

/**
 * Legende d'un indicateur. Elle est deduite de la SPECIFICATION seule, jamais
 * de ce qui est affiche a l'ecran : legende et carte ne peuvent donc pas
 * diverger, elles sont deux lectures de la meme description.
 */
export function buildLegend(spec) {
  if (!spec) return null;

  if (spec.kind === "quantitative") {
    const { min, max } = spec.domain || { min: 0, max: 1 };
    return {
      kind: "quantitative",
      min, max,
      scale: spec.scale,
      unit: spec.unit,
      format: spec.format,
      gradient: rampToCssGradient(spec),
      // Une echelle divergente doit annoncer son point neutre, sinon le
      // milieu de la rampe passe pour une valeur mediane quelconque.
      neutral: spec.scale === "diverging" ? 0 : null,
    };
  }

  if (spec.kind === "boolean") {
    return {
      kind: "category",
      entries: [
        { value: true, label: spec.trueLabel || "Oui", color: BOOLEAN_COLORS.true },
        { value: false, label: spec.falseLabel || "Non", color: BOOLEAN_COLORS.false },
      ],
    };
  }

  const levels = spec.levels || [];

  if (spec.kind === "ordinal") {
    // La rampe est repartie le long de l'ordre declare : c'est l'ordre qui
    // porte le sens d'une echelle de tension ou de risque, pas l'effectif.
    const ramp = rampFor(spec);
    return {
      kind: "ordinal",
      entries: levels.map((level, i) => ({
        ...level,
        color: rampHex(levels.length > 1 ? i / (levels.length - 1) : 0, ramp),
      })),
    };
  }

  let fallbackIndex = 0;
  return {
    kind: "category",
    entries: levels.map((level) => ({
      ...level,
      color: level.color || CATEGORICAL_FALLBACK[fallbackIndex++ % CATEGORICAL_FALLBACK.length],
    })),
  };
}

/**
 * Position 0-1 d'une valeur dans l'echelle d'un indicateur quantitatif.
 *   log       : quelques entites concentrent l'essentiel (population,
 *               surfaces) — en lineaire tout le territoire serait d'une meme
 *               couleur pale sauf trois taches.
 *   diverging : centre sur 0 et symetrique, pour que le neutre tombe pile au
 *               milieu de la rampe quel que soit le desequilibre des bornes.
 */
export function normalizeValue(value, spec) {
  const { min, max } = spec.domain || { min: 0, max: 1 };
  if (spec.scale === "log") {
    const lo = Math.log10(Math.max(min, 1));
    const hi = Math.log10(Math.max(max, min + 1, 1));
    return (Math.log10(Math.max(value, 1)) - lo) / (hi - lo || 1);
  }
  if (spec.scale === "diverging") {
    const half = Math.max(Math.abs(min), Math.abs(max)) || 1;
    return 0.5 + value / (2 * half);
  }
  return (value - min) / (max - min || 1);
}

/**
 * Une couleur THREE par entite (index = index dans `records`, donc la valeur
 * stockee dans le raster de la couche).
 *
 * Les couleurs categorielles viennent de la LEGENDE, pas des enregistrements :
 * une modalite sans couleur de source recoit la meme teinte de secours dans
 * les deux, ce qu'un tirage independant ne garantirait pas.
 */
export function indicatorColors(records, spec, legend) {
  const fallback = new THREE.Color(NODATA_COLOR);
  if (!spec) return (records || []).map(() => fallback);

  if (spec.kind === "quantitative") {
    const ramp = rampFor(spec);
    return (records || []).map((record) => {
      const value = record[spec.id];
      if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
      return rampColorAt(normalizeValue(value, spec), ramp);
    });
  }

  const colorByValue = new Map();
  (legend?.entries || []).forEach((entry) => colorByValue.set(entry.value, new THREE.Color(entry.color)));
  return (records || []).map((record) => {
    const value = record[spec.id];
    if (value == null) return fallback;
    return colorByValue.get(value) || fallback;
  });
}

/**
 * Tout ce qu'il faut pour afficher un indicateur d'une couche : la
 * specification retenue, sa legende et une couleur par entite.
 *
 * Les couches communes et geologie ont exactement la meme forme de datapack
 * ({ indicators, default_indicator, <entites> }) et le meme raster d'index :
 * elles passent donc toutes deux par ici, et toute couche future qui
 * produira des specifications du meme format en heritera sans code nouveau.
 */
export function resolveIndicator(specs, records, indicatorId) {
  const spec = findIndicator(specs, indicatorId);
  if (!spec) return { spec: null, legend: null, colors: [] };
  const legend = buildLegend(spec);
  return { spec, legend, colors: indicatorColors(records, spec, legend) };
}
